import { useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Store as StoreIcon,
  PackageOpen,
  Sparkles,
  Wrench,
  Megaphone,
  FolderKanban,
  FilePen,
  Send,
  ArchiveX,
  User as UserIcon,
  Bell,
  Plug,
  Shield,
} from 'lucide-react';
import { cn, IconButton } from '@/components/ui';

/*
  SecondaryPanel — контекстная навигация. Сидит на shell-bg без своего bg.

  Behaviors:
  - Items с `children` → drill-down (полная замена контента через fade
    + breadcrumb «← Parent»)
  - Items без children → terminal selection (просто active state)
  - Drill-down state показывает children с TREE-LINE CONNECTOR
    (вертикальная линия + branch'и через ::before pseudo-elements)
  - Icons everywhere (Sprint 1.8 fix — раньше были plain text)

  Animations:
  - Width: 0 ↔ 256 (visible toggle), 256 ↔ 64 (collapsed toggle)
  - Content swap на drill-down: fade-in 200ms ease-out
  - Sprint 1.14: JS-based sliding indicator (white card морфит между
    item'ами при select). Тот же паттерн что и в PrimaryRail —
    data-active="true" + ResizeObserver + absolute box с CSS transitions.
*/

interface SecondaryPanelProps {
  visible: boolean;
  collapsed: boolean;
  /**
   * Sprint 1.16: вызывается когда юзер выбирает TERMINAL item (без
   * children → finalized навигация по фильтру). AppShell использует это
   * для auto-collapse rail'а — после commit'а на контекст rail прячется
   * чтобы дать место канвасу. Drill-down item'ы НЕ триггерят.
   */
  onTerminalSelect?: () => void;
}

interface IndicatorBox {
  top: number;
  left: number;
  width: number;
  height: number;
  visible: boolean;
}

export function SecondaryPanel({ visible, collapsed, onTerminalSelect }: SecondaryPanelProps) {
  const location = useLocation();
  const [drilldown, setDrilldown] = useState<DrilldownState | null>(null);
  const [activeKey, setActiveKey] = useState<string>('all');

  const config = panelConfigForRoute(location.pathname);

  // ----- Sliding indicator (Sprint 1.14) -----
  const navRef = useRef<HTMLElement>(null);
  const [indicator, setIndicator] = useState<IndicatorBox>({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    visible: false,
  });
  const [hasInitialized, setHasInitialized] = useState(false);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const measure = () => {
      const activeEl = nav.querySelector<HTMLElement>('[data-secondary-active="true"]');
      if (activeEl) {
        const navRect = nav.getBoundingClientRect();
        const itemRect = activeEl.getBoundingClientRect();
        setIndicator({
          top: itemRect.top - navRect.top,
          left: itemRect.left - navRect.left,
          width: itemRect.width,
          height: itemRect.height,
          visible: true,
        });
      } else {
        setIndicator((prev) => ({ ...prev, visible: false }));
      }
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(nav);
    nav.querySelectorAll('[data-secondary-active="true"]').forEach((el) => ro.observe(el));

    return () => ro.disconnect();
  }, [activeKey, drilldown, collapsed, visible, config.title]);

  useEffect(() => {
    if (indicator.visible && !hasInitialized) {
      const t = window.setTimeout(() => setHasInitialized(true), 50);
      return () => window.clearTimeout(t);
    }
  }, [indicator.visible, hasInitialized]);

  // Сбрасываем active key при смене route (config меняется → активный key
  // может быть нерелевантным)
  const [previousTitle, setPreviousTitle] = useState(config.title);
  if (previousTitle !== config.title) {
    setPreviousTitle(config.title);
    setActiveKey('all');
    setDrilldown(null);
  }

  return (
    <nav
      ref={navRef}
      data-collapsed={collapsed || undefined}
      data-visible={visible || undefined}
      className={cn(
        'relative flex shrink-0 flex-col overflow-hidden',
        // 400ms ease-out — медленнее (Sprint 1.10, в синк с rail)
        'transition-panel-width duration-slow ease-out',
        !visible ? '-mr-3 w-0 opacity-0' : collapsed ? 'w-16 opacity-100' : 'w-64 opacity-100',
      )}
      style={{ viewTransitionName: 'app-secondary' }}
      aria-label={config.title}
      aria-hidden={!visible}
    >
      {/* Sliding indicator — white card морфит между active item'ами */}
      <div
        aria-hidden
        className="pointer-events-none absolute z-base rounded-lg bg-card"
        style={{
          top: `${indicator.top}px`,
          left: `${indicator.left}px`,
          width: `${indicator.width}px`,
          height: `${indicator.height}px`,
          opacity: indicator.visible ? 1 : 0,
          boxShadow: 'var(--shadow-xs)',
          transition: hasInitialized
            ? 'top 320ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
              'left 320ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
              'width 320ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
              'height 320ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
              'opacity 200ms ease-out'
            : 'opacity 200ms ease-out',
        }}
      />

      {/* Header */}
      <div
        className={cn(
          'relative z-raised flex items-center justify-between gap-2 px-3 pt-3 pb-4 transition-opacity duration-normal',
          collapsed && 'pointer-events-none opacity-0',
        )}
      >
        {drilldown ? (
          <button
            type="button"
            onClick={() => setDrilldown(null)}
            className="-ml-2 flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-fg-muted transition-colors hover:bg-active hover:text-fg dark:hover:bg-white/5"
          >
            <ChevronLeft size={14} />
            <span>{drilldown.parentLabel}</span>
          </button>
        ) : (
          <h2 className="text-base font-semibold tracking-tight">{config.title}</h2>
        )}
        <IconButton aria-label="Поиск в разделе" variant="ghost" size="sm">
          <Search />
        </IconButton>
      </div>

      {/* Body — fade transition при drill-down swap */}
      <div
        key={drilldown?.key ?? 'root'}
        className="relative z-raised flex-1 animate-fade-in overflow-y-auto px-2"
      >
        <PanelContent
          groups={drilldown ? drilldown.groups : config.items}
          activeKey={activeKey}
          onSelect={(item) => {
            if (item.children) {
              setDrilldown({
                key: item.key,
                parentLabel: drilldown?.parentLabel ?? config.title,
                groups: item.children,
              });
            } else {
              setActiveKey(item.key);
              // Sprint 1.16: terminal commit → AppShell auto-collapses rail.
              onTerminalSelect?.();
            }
          }}
        />
      </div>
    </nav>
  );
}

// ----- Types -----

interface PanelItem {
  key: string;
  label: string;
  icon?: ReactNode;
  count?: number;
  badge?: 'coral';
  /** Если есть — drill-down. */
  children?: PanelGroup[];
}

interface PanelGroup {
  label?: string;
  items: PanelItem[];
}

interface PanelConfig {
  title: string;
  items: PanelGroup[];
}

interface DrilldownState {
  key: string;
  parentLabel: string;
  groups: PanelGroup[];
}

// ----- Content with optional tree-line -----

function PanelContent({
  groups,
  activeKey,
  onSelect,
}: {
  groups: PanelGroup[];
  activeKey: string;
  onSelect: (item: PanelItem) => void;
}) {
  return (
    <div className="flex flex-col">
      {groups.map((group, gi) => (
        <div key={gi} className={cn('relative px-1 py-2', gi > 0 && 'mt-1 border-t border-line-strong pt-3')}>
          {group.label && (
            <h3 className="mb-1.5 px-2 text-2xs font-semibold tracking-wider text-fg-subtle uppercase">
              {group.label}
            </h3>
          )}
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => (
              <li key={item.key}>
                <PanelItemView item={item} active={item.key === activeKey} onSelect={onSelect} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function PanelItemView({
  item,
  active,
  onSelect,
}: {
  item: PanelItem;
  active: boolean;
  onSelect: (item: PanelItem) => void;
}) {
  const hasChildren = !!item.children;
  // Sprint 1.14: active item НЕ имеет своего bg — indicator (absolute white
  // card) сидит под ним. Чтобы text не дёргался при transition мы оставляем
  // text-color/font-weight в active state, но bg/shadow убираем.
  const dataAttrs = active && !hasChildren ? { 'data-secondary-active': 'true' } : {};
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      {...dataAttrs}
      className={cn(
        'group/item relative z-raised flex h-10 w-full items-center gap-2 rounded-lg px-3 text-sm outline-none',
        'transition-single-color duration-normal ease-out',
        active && !hasChildren ? 'font-medium text-fg' : 'text-fg hover:bg-active/60 dark:hover:bg-white/8',
      )}
    >
      {item.icon && (
        <span
          className={cn(
            'shrink-0 [&_svg]:size-4.5',
            'transition-colors duration-normal',
            active && !hasChildren ? 'text-brand-accent' : 'text-fg-muted',
          )}
        >
          {item.icon}
        </span>
      )}
      <span className="flex-1 truncate text-left">{item.label}</span>
      {item.count !== undefined && (
        <span
          className={cn(
            'shrink-0 text-xs tabular-nums',
            item.count === 0 && 'opacity-50',
            item.badge === 'coral' && item.count > 0
              ? 'rounded-xs bg-brand-accent px-1.5 py-0.5 text-2xs font-semibold text-white opacity-100'
              : 'text-fg-subtle',
          )}
        >
          {item.count > 99 ? '99+' : item.count}
        </span>
      )}
      {hasChildren && (
        <ChevronRight
          size={14}
          className="shrink-0 text-fg-subtle opacity-0 transition-opacity group-hover/item:opacity-100"
        />
      )}
    </button>
  );
}

// ----- Per-route config — теперь с иконками для каждого item'а -----

function panelConfigForRoute(path: string): PanelConfig {
  if (path.startsWith('/marketplace')) {
    return {
      title: 'Marketplace',
      items: [
        {
          items: [
            { key: 'all', label: 'Все модули', icon: <StoreIcon />, count: 0 },
            { key: 'installed', label: 'Установлено мной', icon: <PackageOpen />, count: 0 },
            { key: 'recommended', label: 'Рекомендуем', icon: <Sparkles /> },
          ],
        },
        {
          label: 'Категории',
          items: [
            { key: 'infra', label: 'Инфраструктура', icon: <Wrench /> },
            { key: 'ai', label: 'AI-инструменты', icon: <Sparkles /> },
            { key: 'comm', label: 'Коммуникации', icon: <Megaphone /> },
            { key: 'admin', label: 'Администрирование', icon: <Shield /> },
          ],
        },
      ],
    };
  }
  if (path.startsWith('/portfolio')) {
    return {
      title: 'Моё портфолио',
      items: [
        {
          items: [
            { key: 'all', label: 'Все мои модули', icon: <FolderKanban />, count: 0 },
            { key: 'drafts', label: 'Черновики', icon: <FilePen />, count: 0 },
            { key: 'published', label: 'Опубликовано', icon: <Send />, count: 0 },
            { key: 'archived', label: 'Архив', icon: <ArchiveX />, count: 0 },
          ],
        },
      ],
    };
  }
  if (path.startsWith('/settings')) {
    return {
      title: 'Настройки',
      items: [
        {
          items: [
            { key: 'profile', label: 'Профиль', icon: <UserIcon /> },
            { key: 'notifications', label: 'Уведомления', icon: <Bell /> },
            { key: 'integrations', label: 'Интеграции', icon: <Plug /> },
            { key: 'security', label: 'Безопасность', icon: <Shield /> },
          ],
        },
      ],
    };
  }
  return {
    title: 'Раздел',
    items: [{ items: [] }],
  };
}
